import React, { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "../../../../ui/components/Button";
import { Table } from "../../../../ui/components/Table";
import { TextField } from "../../../../ui/components/TextField";
import { Loader } from "../../../../ui/components/Loader";
import { DialogLayout } from "../../../../ui/layouts/DialogLayout";
import TableSkeletonRows from "../../../../shared/presentation/components/TableSkeletonRows";
import { ReviewRequestModal } from "../../../../shared/presentation/components/ReviewRequestModal";
import { useToast } from "../../../../shared/presentation/components";
import { usePermissions } from "../../../../shared/presentation/hooks/usePermissions";
import {
  MODULE_CODES,
  ACTION_CODES,
} from "../../../../shared/constants/permissions";
import {
  FeatherPlus,
  FeatherSearch,
  FeatherChevronLeft,
  FeatherChevronRight,
  FeatherEdit,
  FeatherTrash2,
  FeatherAlertTriangle,
  FeatherUpload,
} from "@subframe/core";
import { useGls } from "../hooks/useGls";
import { useGlManagementTree } from "../hooks/useGlManagementTree";
import { checkGlCanDelete } from "../../data/api/glsApi";
import { glTreeNodeToEntity } from "../../data/mappers/GlManagementTreeMapper";
import type { GlTreeNode } from "../../data/mappers/GlManagementTreeMapper";
import { GlFormModal } from "./GlFormModal";
import type { GlFormSuccessParams } from "./GlFormModal";
import { GlGroupFormModal } from "./GlGroupFormModal";
import type { GlGroupFormSuccessParams } from "./GlGroupFormModal";
import type { GlEntity } from "../../domain/entities/GlEntity";
import {
  GlManagementTreeRows,
  GlManagementTreeTableHeader,
  renderYesNoFlag,
} from "./GlManagementTreeRows";
import { BulkUploadModal } from "../../../masters/presentation/components/BulkUploadModal";
import { getStoredAccountingBackend } from "../../../../config/accounting";
import { isReservedTallyPrimaryGroupName } from "../utils/tallyGlDefaults";

type ListLoadingReason = "initial" | "pagination" | "search" | null;

const TABLE_COLUMN_COUNT = 6;

export interface GlsSectionProps {
  /**
   * Render the "General Ledger" heading and Add GL button.
   *
   * Off when the host already supplies a heading — the settings area's Chart of
   * Accounts panel does, and two headings stacked read as two screens. The Add
   * action moves up beside the host's heading in that case, which is why it is
   * offered back through `renderCreateAction`.
   */
  showHeader?: boolean;
  /** Wrapper classes, so an embedded copy can drop the standalone page padding. */
  className?: string;
}

export const GlsSection: React.FC<GlsSectionProps> = ({
  showHeader = true,
  className = "flex w-full flex-col items-start gap-6 p-8",
}) => {
  const { deleteGl } = useGls();
  const {
    items,
    totalItems,
    hasMore,
    loading,
    error,
    page,
    setPage,
    searchQuery,
    setSearchQuery,
    refetch,
    pageSize,
  } = useGlManagementTree();

  const { getPermissionInfo } = usePermissions();
  const { showSuccess, showError } = useToast();

  const canCreate = getPermissionInfo(
    MODULE_CODES.GL,
    ACTION_CODES.CREATE,
  ).granted;
  const canEdit = getPermissionInfo(MODULE_CODES.GL, ACTION_CODES.EDIT).granted;
  const canDelete = getPermissionInfo(
    MODULE_CODES.GL,
    ACTION_CODES.DELETE,
  ).granted;
  const bulkUploadPerm = getPermissionInfo(
    MODULE_CODES.GL,
    ACTION_CODES.BULK_UPLOAD,
  );
  const deleteRequiresReview = getPermissionInfo(
    MODULE_CODES.GL,
    ACTION_CODES.DELETE,
  ).requiresReview;

  const isTallyBackend = getStoredAccountingBackend() === "tally";
  const showBulkUpload = bulkUploadPerm.granted;

  const [loadingReason, setLoadingReason] =
    useState<ListLoadingReason>("initial");

  const [formModalOpen, setFormModalOpen] = useState(false);
  const [groupFormModalOpen, setGroupFormModalOpen] = useState(false);
  const [editingGl, setEditingGl] = useState<GlEntity | null>(null);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [glToDelete, setGlToDelete] = useState<GlEntity | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [checkingDeleteId, setCheckingDeleteId] = useState<string | null>(null);

  const [deleteReviewModalOpen, setDeleteReviewModalOpen] = useState(false);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);

  const loadingStartedRef = useRef(false);
  const prevSearchRef = useRef(searchQuery);
  const prevPageRef = useRef(page);

  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const displayPage = page + 1;
  const showingFrom = totalItems === 0 ? 0 : page * pageSize + 1;
  const showingTo = Math.min((page + 1) * pageSize, totalItems);

  useEffect(() => {
    if (loading) {
      loadingStartedRef.current = true;
      return;
    }

    if (loadingStartedRef.current) {
      loadingStartedRef.current = false;
      setLoadingReason(null);
    }
  }, [loading]);

  useEffect(() => {
    if (prevSearchRef.current !== searchQuery) {
      setLoadingReason("search");
      prevSearchRef.current = searchQuery;
    }
  }, [searchQuery]);

  useEffect(() => {
    if (prevPageRef.current !== page) {
      setLoadingReason("pagination");
      prevPageRef.current = page;
    }
  }, [page]);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchQuery(value);
    },
    [setSearchQuery],
  );

  const handlePageChange = useCallback(
    (nextPage: number) => {
      setLoadingReason("pagination");
      setPage(nextPage - 1);
    },
    [setPage],
  );

  const handleAddGl = () => {
    setEditingGl(null);
    setFormModalOpen(true);
  };

  const handleAddGroup = () => {
    setEditingGl(null);
    setGroupFormModalOpen(true);
  };

  const handleEditGl = (node: GlTreeNode, parent?: GlTreeNode) => {
    const entity = glTreeNodeToEntity(node, parent);
    setEditingGl(entity);
    if (node.isGroup && isTallyBackend) {
      setGroupFormModalOpen(true);
    } else {
      setFormModalOpen(true);
    }
  };

  const handleFormSuccess = useCallback(
    async (_params: GlFormSuccessParams) => {
      setFormModalOpen(false);
      setEditingGl(null);
      await refetch();
    },
    [refetch],
  );

  const handleGroupFormSuccess = useCallback(
    async (_params: GlGroupFormSuccessParams) => {
      setGroupFormModalOpen(false);
      setEditingGl(null);
      await refetch();
    },
    [refetch],
  );

  const handleDeleteClick = async (node: GlTreeNode, parent?: GlTreeNode) => {
    const gl = glTreeNodeToEntity(node, parent);
    setCheckingDeleteId(gl.accountId);
    try {
      const result = await checkGlCanDelete(gl.accountId);
      if (!result.can_delete) {
        showError(
          result.message ||
            "This account has associated transactions and cannot be deleted.",
        );
        return;
      }
      setGlToDelete(gl);
      setDeleteDialogOpen(true);
    } catch (err: unknown) {
      showError(
        err instanceof Error
          ? err.message
          : "Unable to verify if GL can be deleted",
      );
    } finally {
      setCheckingDeleteId(null);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!glToDelete) return;

    if (deleteRequiresReview) {
      setDeleteDialogOpen(false);
      setDeleteReviewModalOpen(true);
      return;
    }

    setIsDeleting(true);
    try {
      await deleteGl(glToDelete.accountId);
      setDeleteDialogOpen(false);
      setGlToDelete(null);
      showSuccess("GL deleted successfully");
      await refetch();
    } catch (err: unknown) {
      showError(err instanceof Error ? err.message : "Failed to delete GL");
    } finally {
      setIsDeleting(false);
    }
  };

  const renderActions = useCallback(
    (node: GlTreeNode, parent?: GlTreeNode) => {
      if (node.status === "under_review") {
        return null;
      }

      // Tally reserved primary groups: view-only (backend also rejects mutate).
      const isReservedGroup =
        isTallyBackend &&
        node.isGroup &&
        isReservedTallyPrimaryGroupName(node.glName);

      if (isReservedGroup) {
        return null;
      }

      // Parents/groups with children: edit+reparent OK; delete only when empty.
      const canShowDelete = !node.hasChildren;

      return (
        <div className="flex items-center gap-2">
          {canEdit && (
            <Button
              variant="neutral-tertiary"
              size="small"
              icon={<FeatherEdit />}
              onClick={() => handleEditGl(node, parent)}
            >
              Edit
            </Button>
          )}
          {canDelete && canShowDelete && (
            <Button
              variant="neutral-tertiary"
              size="small"
              icon={<FeatherTrash2 />}
              loading={checkingDeleteId === node.accountId}
              disabled={checkingDeleteId !== null}
              onClick={() => void handleDeleteClick(node, parent)}
            >
              Delete
            </Button>
          )}
        </div>
      );
    },
    [canEdit, canDelete, checkingDeleteId, isTallyBackend],
  );

  const showTableSkeleton =
    loadingReason === "initial" ||
    loadingReason === "pagination" ||
    (loading && items.length === 0);
  const showInlineLoading =
    !showTableSkeleton && (loadingReason === "search" || loading);

  const createActions = canCreate ? (
    <div className="flex items-center gap-2">
      {showBulkUpload ? (
        <Button
          variant="neutral-secondary"
          icon={<FeatherUpload />}
          onClick={() => setBulkUploadOpen(true)}
        >
          Bulk upload
        </Button>
      ) : null}
      {isTallyBackend && (
        <Button
          variant="neutral-secondary"
          icon={<FeatherPlus />}
          onClick={handleAddGroup}
        >
          Add Group
        </Button>
      )}
      <Button icon={<FeatherPlus />} onClick={handleAddGl}>
        Add GL
      </Button>
    </div>
  ) : null;

  return (
    <div className={className}>
      {showHeader && (
        <div className="flex w-full items-center justify-between">
          <div className="flex flex-col items-start gap-1">
            <span className="text-heading-2 font-heading-2 text-default-font">
              General Ledger
            </span>
            <span className="text-body font-body text-subtext-color">
              Manage chart of accounts
            </span>
          </div>
          {createActions}
        </div>
      )}

      <div className="flex w-full items-center justify-between gap-4">
        <TextField
          className="h-auto w-96 flex-none"
          variant="filled"
          icon={<FeatherSearch />}
        >
          <TextField.Input
            placeholder="Search GLs..."
            value={searchQuery}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleSearchChange(e.target.value)
            }
          />
        </TextField>
        <div className="flex items-center gap-4">
          <span className="text-body font-body text-subtext-color whitespace-nowrap">
            {totalItems} account group{totalItems !== 1 ? "s" : ""} in total
          </span>
          {/* With the heading hidden the Add action has nowhere else to sit. */}
          {!showHeader && createActions}
        </div>
      </div>

      {error && (
        <div className="flex w-full rounded-lg border border-solid border-red-200 bg-red-50 p-4">
          <span className="text-body font-body text-red-600">{error}</span>
        </div>
      )}

      <div className="w-full overflow-x-auto">
        <Table
          header={<GlManagementTreeTableHeader showActionsColumn={true} />}
        >
          {showTableSkeleton ? (
            <TableSkeletonRows columnCount={TABLE_COLUMN_COUNT} rowCount={10} />
          ) : showInlineLoading ? (
            <Table.Row>
              <Table.Cell colSpan={TABLE_COLUMN_COUNT}>
                <div className="flex w-full items-center justify-center py-10">
                  <Loader />
                </div>
              </Table.Cell>
            </Table.Row>
          ) : items.length === 0 ? (
            <Table.Row>
              <Table.Cell colSpan={TABLE_COLUMN_COUNT}>
                <div className="flex w-full items-center justify-center py-10">
                  <span className="text-body font-body text-subtext-color">
                    {searchQuery.trim()
                      ? "No GLs match your search"
                      : "No GL accounts found"}
                  </span>
                </div>
              </Table.Cell>
            </Table.Row>
          ) : (
            <GlManagementTreeRows
              nodes={items}
              renderBill={(node) =>
                node.isGroup || node.hasChildren
                  ? null
                  : renderYesNoFlag(node.bill)
              }
              renderInvoice={(node) =>
                node.isGroup || node.hasChildren
                  ? null
                  : renderYesNoFlag(node.invoice)
              }
              renderActions={renderActions}
            />
          )}
        </Table>
      </div>

      {!showTableSkeleton && !showInlineLoading && totalItems > pageSize && (
        <div className="flex w-full items-center justify-between">
          <span className="text-body font-body text-subtext-color">
            Showing {showingFrom} – {showingTo} of {totalItems}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="neutral-tertiary"
              size="small"
              icon={<FeatherChevronLeft />}
              onClick={() => handlePageChange(Math.max(1, displayPage - 1))}
              disabled={page === 0}
            >
              Previous
            </Button>
            <span className="text-body font-body text-default-font px-2">
              Page {displayPage} of {totalPages}
            </span>
            <Button
              variant="neutral-tertiary"
              size="small"
              icon={<FeatherChevronRight />}
              onClick={() => handlePageChange(displayPage + 1)}
              disabled={!hasMore}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      <GlFormModal
        open={formModalOpen}
        onOpenChange={setFormModalOpen}
        onSuccess={handleFormSuccess}
        mode={editingGl ? "edit" : "create"}
        initialGl={editingGl}
      />

      {isTallyBackend && (
        <GlGroupFormModal
          open={groupFormModalOpen}
          onOpenChange={setGroupFormModalOpen}
          onSuccess={handleGroupFormSuccess}
          mode={editingGl?.isGroup ? "edit" : "create"}
          initialGl={editingGl?.isGroup ? editingGl : null}
        />
      )}

      {deleteDialogOpen && glToDelete && (
        <DialogLayout
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
        >
          <div className="flex w-120 flex-col items-start gap-6 bg-white px-8 py-8">
            <div className="flex items-start gap-4">
              <div className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-error-100">
                <FeatherAlertTriangle className="text-error-600" />
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-heading-3 font-heading-3 text-default-font">
                  Delete GL Account?
                </span>
                <span className="text-body font-body text-subtext-color">
                  This will permanently remove{" "}
                  <strong>{glToDelete.glName}</strong> from Zoho Books and
                  cannot be undone.
                </span>
              </div>
            </div>
            <div className="flex w-full items-center justify-end gap-3 border-t border-solid border-neutral-border pt-4">
              <Button
                variant="neutral-tertiary"
                onClick={() => {
                  setDeleteDialogOpen(false);
                  setGlToDelete(null);
                }}
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <Button
                variant="destructive-primary"
                onClick={() => void handleDeleteConfirm()}
                loading={isDeleting}
                disabled={isDeleting}
              >
                {deleteRequiresReview ? "Submit for Review" : "Delete"}
              </Button>
            </div>
          </div>
        </DialogLayout>
      )}

      {glToDelete && (
        <ReviewRequestModal
          open={deleteReviewModalOpen}
          onOpenChange={setDeleteReviewModalOpen}
          moduleCode="gl"
          actionCode="delete"
          entityType="gl"
          entityId={glToDelete.id}
          title="GL Deletion"
          changeDetails={{
            action_types: ["delete"],
            raw_json: {
              account_id: glToDelete.accountId,
              account_name: glToDelete.glName,
              account_type: glToDelete.accountType,
              parent_account_id: glToDelete.parentId ?? null,
              parent_account_name: glToDelete.parentName ?? null,
              invoice: glToDelete.invoice ? 1 : 0,
              bill: glToDelete.bill ? 1 : 0,
            },
          }}
          skipNavigation={true}
          onSuccess={async () => {
            setDeleteReviewModalOpen(false);
            setGlToDelete(null);
            await refetch();
          }}
        />
      )}

      {showBulkUpload && (
        <BulkUploadModal
          open={bulkUploadOpen}
          onOpenChange={setBulkUploadOpen}
          kind="gls"
          requiresReview={bulkUploadPerm.requiresReview}
          moduleCode={MODULE_CODES.GL}
          entityType="gl"
          onUploadComplete={() => void refetch()}
        />
      )}
    </div>
  );
};
